import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { t } from '@lingui/core/macro';
import { ToastProps } from '../../types/toast';
import Icon from './Icon';

function Toast({ message, type, duration = 3000, onClose }: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setIsVisible(true));

    const timer = setTimeout(() => {
      setIsVisible(false);
      setTimeout(onClose, 200); // wait for exit animation
    }, duration);

    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return 'checkmark';
      case 'error':
        return 'close';
      case 'message':
        return 'exclamation';
      default:
        return 'info';
    }
  };

  // The screen-reader announcement is handled by the persistent live regions in
  // ToastProvider (Chrome + VoiceOver don't reliably announce a freshly-portaled
  // role="alert"). This node is the visual presentation only, so it deliberately
  // carries no live-region role, to avoid a double announcement in browsers that
  // do honor alert-on-insert.
  return createPortal(
    <div
      className={clsx(
        'z-toast fixed top-4 right-4 flex max-w-sm items-center gap-3 rounded-lg border px-4 py-3 shadow-lg transition-all duration-200',
        isVisible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0',
        type === 'success' && 'border-success bg-surface-success-light text-success',
        type === 'error' && 'border-danger bg-surface-danger-light text-danger',
        type === 'message' && 'border-warning bg-surface-warning-light text-warning',
        (type === 'info' || !type) && 'border-info bg-surface-info-light text-info',
      )}
    >
      <div
        className={clsx(
          'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full',
          type === 'success' && 'bg-surface-success',
          type === 'error' && 'bg-surface-danger',
          type === 'message' && 'bg-surface-warning',
          (type === 'info' || !type) && 'bg-surface-info',
        )}
      >
        <Icon name={getIcon()} size="sm" className="text-white" />
      </div>
      <span className="flex-1 text-sm font-medium">{message}</span>
      <button
        onClick={() => {
          setIsVisible(false);
          setTimeout(onClose, 200);
        }}
        className="ml-2 flex-shrink-0 rounded p-0.5 opacity-60 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-current focus:ring-offset-1 focus:outline-none"
        aria-label={t`Close`}
      >
        <Icon name="close" size="sm" />
      </button>
    </div>,
    document.body,
  );
}

export default Toast;
